<template>
  <ExportData :isOpen="isExportModalOpen" @closeModal="closeExportModal" :selectedArea="selectedArea" />
  <BaseCard id="rate-connect-performance" noPadding class="tw:bg-white tw:rounded-lg">
    <div v-if="(hasNoBookings && selectedArea === 'bookings') || (hasNoData && selectedArea === 'overview')">
      <div v-if="hasNoBookings && selectedArea === 'bookings'" class="tw:mx-4 tw:py-6">
        <PerformanceEmptyWrapper :missingData="true" :type="'bookings'" />
      </div>
      <div v-if="hasNoData && selectedArea === 'overview'" class="tw:mx-4 tw:py-6">
        <PerformanceEmptyWrapper :missingData="true" :type="'overview'" />
      </div>
    </div>
    <div
      v-else
      class="tw:mx-4 tw:py-6 tw:flex tw:justify-between tw:flex-col tw:border-b tw:border-gray-300 tw:mb-6 tw:rounded-lg">
      <div class="tw:flex tw:justify-between" id="rate-connect-filters">
        <PerformanceFilters
          :overviewTableCampaignType="overviewTableCampaignType"
          :markets="markets"
          :selectedArea="selectedArea"
          :selectedDisplayType="selectedDisplayType"
          :columns="columns" />

        <div class="tw:flex tw:flex-row tw:gap-4">
          <GhostButton
            @click="changeDisplayType"
            v-if="selectedArea === 'overview'"
            class="tw:flex tw:items-center tw:gap-2">
            <GraphIcon v-if="selectedDisplayType === 'table'" class="table tw:fill-gray-800 tw:size-[18px]" />
            <TableIcon v-if="selectedDisplayType === 'graph'" class="graph tw:stroke-gray-800 tw:size-[18px]" />
            {{
              selectedDisplayType === 'table' ? $t('rc_label_viewas_graph') : $t('rc_label_viewas_table')
            }}</GhostButton
          >
          <MainButton
            :disabled="!isActive && ((hasNoData && selectedArea === 'overview') || hasEmptyStatsTable)"
            @click="openExportModal"
            class="tw:flex tw:items-center tw:gap-2">
            <DownloadIcon />
            {{ $t('rc_data_download_cta') }}
          </MainButton>
        </div>
      </div>
      <div
        class="tw:mt-6"
        v-if="
          (selectedArea === 'overview' && hasEmptyStatsTable) || (selectedArea === 'bookings' && hasEmptyBookingTable)
        ">
        <PerformanceEmptyWrapper type="filters" />
      </div>
    </div>

    <div>
      <div
        v-if="selectedArea === 'overview'"
        class="tw:px-4 tw:flex tw:gap-x-2 tw:md:gap-2"
        :class="{ 'tw:md:flex-wrap tw:landing:flex-nowrap': kpiTabs.length > 3 }">
        <PerformanceCard
          v-for="(item, index) in kpiTabs"
          :hasEmptyTable="hasEmptyStatsTable"
          :hasNoData="hasNoData"
          :id="`${item.key}-tab`"
          :key="item.key"
          :elementId="item.key"
          :label="item.label"
          :number="item.number"
          :index="index"
          :progression="item.progression"
          role="tab"
          :data-tabs-target="`#${item.key}`"
          :class="{ 'tw:md:w-[49%] tw:lg:w-1/4': kpiTabs.length > 3 }" />
      </div>

      <div
        v-if="selectedArea === 'bookings'"
        class="tw:px-4 tw:flex tw:gap-x-2 tw:md:gap-2"
        :class="{ 'tw:md:flex-wrap tw:lg:flex-nowrap': bookingKpis.length > 3 }">
        <BookingCard
          v-for="(item, index) in bookingKpis"
          :subtitle="item.subtitle"
          :subtitleText="item.subtitleText"
          :hasEmptyTable="hasEmptyBookingTable"
          :hasNoData="hasNoData"
          :id="`${item.id}-tab`"
          :key="item.id"
          :elementId="item.id"
          :label="item.label"
          :number="item.number"
          :index="index"
          :progression="item.progression"
          role="tab"
          :data-tabs-target="`#${item.id}`"
          :class="{ 'tw:md:w-[49%] tw:lg:w-1/4': bookingKpis.length > 3 }" />
      </div>
      <div class="tw:mt-6">
        <div v-if="selectedArea === 'overview'">
          <OverviewTable
            v-if="selectedDisplayType === 'table'"
            :overviewTableCampaignType="overviewTableCampaignType"
            :hasEmptyTable="hasEmptyStatsTable || hasNoData" />

          <ClickStats
            v-if="selectedDisplayType === 'graph' && selectedGraph === 'clicks'"
            class="tw:p-6 tw:pt-0"
            :categories="categories"
            :campaignTypes="campaignTypes"
            :tickPositions="tickPositions"
            :prettyVisits="visits"
            :currency="currency" />
          <RevenueStats
            v-if="selectedDisplayType === 'graph' && selectedGraph === 'revenue'"
            class="tw:p-6 tw:pt-0"
            :categories="categories"
            :campaignTypes="campaignTypes"
            :tickPositions="tickPositions"
            :prettyRevenue="revenue"
            :currency="currency" />

          <CpaStats
            v-if="selectedDisplayType === 'graph' && selectedGraph === 'cpa'"
            class="tw:p-6 tw:pt-0"
            :categories="categories"
            :campaignTypes="campaignTypes"
            :tickPositions="tickPositions"
            :prettyCpa="cpa"
            :currency="currency" />
          <BookingStats
            v-if="selectedDisplayType === 'graph' && selectedGraph === 'bookings'"
            class="tw:p-6 tw:pt-0"
            :categories="categories"
            :campaignTypes="campaignTypes"
            :tickPositions="tickPositions"
            :prettyBookings="bookings"
            :currency="currency" />
        </div>
        <div v-if="selectedArea === 'bookings'">
          <BookingTable :columns="columns" :hasEmptyTable="hasEmptyBookingTable || hasNoBookings" />
        </div>
      </div>
    </div>
  </BaseCard>
  <p class="tw:text-right tw:w-auto tw:text-sm tw:text-gray-800 tw:mt-6">
    {{ $t('rc_label_last_updated') }}{{ ' ' }}<span>{{ lastUpdated }}</span>
  </p>
</template>
<script setup>
import { computed, ref } from 'vue'

import BaseCard from '@/components/dashboard/BaseCard.vue'
import PerformanceCard from '@/components/rateConnect/performance/PerformanceCard.vue'
import BookingCard from '@/components/rateConnect/performance/BookingCard.vue'
import BookingTable from '@/components/rateConnect/performance/BookingTable.vue'
import OverviewTable from '@/components/rateConnect/performance/OverviewTable.vue'
import PerformanceFilters from '@/components/rateConnect/performance/PerformanceFilters.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import TableIcon from '@/components/icons/TableIcon.svg?component'
import GraphIcon from '@/components/icons/GraphIcon.svg?component'
import ClickStats from '@/components/rateConnect/performance/ClickStats.vue'
import RevenueStats from '@/components/rateConnect/performance/RevenueStats.vue'
import BookingStats from '@/components/rateConnect/performance/BookingStats.vue'
import CpaStats from '@/components/rateConnect/performance/CpaStats.vue'
import PerformanceEmptyWrapper from './PerformanceEmptyWrapper.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import DownloadIcon from '@/components/icons/DownloadIcon.svg?component'
import ExportData from '@/components/rateConnect/performance/ExportData.vue'

import { useRateConnectPerformance } from '@/components/rateConnect/performance/useRateConnectPerformance.js'
import { cpcColumns, cpaColumns, bothColumns } from '@/constants/bookingLogColumns'
import {
  useBookingsAmount,
  useCheckinData,
  useCheckoutData,
  usePrevBookingsAmount,
  usePrevCheckinData,
  usePrevCheckoutData,
} from '../queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { useTour } from '@/composables/useRateConnectTour'
import { useSessionValues } from '@/composables/useSessionValues'

const props = defineProps({
  selectedArea: String,
  currentCampaignType: String,
  prettyKpis: Array,
  lastUpdated: String,
  markets: Array,
  categories: Array,
  visits: Object,
  bookings: Object,
  cpa: Object,
  revenue: Object,
  campaignTypes: Array,
  tickPositions: Array,
  overviewTableCampaignType: String,
  hasEmptyStatsTable: Boolean,
  bookingTableCampaignType: String,
  hasNoData: Boolean,
  hasNoBookings: Boolean,
  hasEmptyBookingTable: Boolean,
})

const { currency } = useSessionValues()
const { isActive } = useTour()

const {
  selectedDisplayType,
  selectedGraph,
  selectedMarket,
  formattedBookingComparisonStartDate,
  formattedBookingComparisonEndDate,
  formattedBookingStartDate,
  formattedBookingEndDate,
  selectedStatus,
  selectedBookingPeriod,
} = useRateConnectPerformance()

const isExportModalOpen = ref(false)
const openExportModal = () => {
  isExportModalOpen.value = true
}
const closeExportModal = () => {
  isExportModalOpen.value = false
}
const changeDisplayType = () => {
  selectedDisplayType.value = selectedDisplayType.value === 'table' ? 'graph' : 'table'
}

const { checkinDataAmount } = useCheckinData(
  selectedAccommodationId,
  currency,
  formattedBookingStartDate,
  formattedBookingEndDate,
  selectedMarket,
  selectedStatus
)

const { prevCheckinDataAmount } = usePrevCheckinData(
  selectedAccommodationId,
  currency,
  formattedBookingComparisonStartDate,
  formattedBookingComparisonEndDate,
  selectedMarket,
  selectedStatus
)

const { checkoutCommissionsAmount, checkoutDataAmount } = useCheckoutData(
  selectedAccommodationId,
  currency,
  formattedBookingStartDate,
  formattedBookingEndDate,
  selectedMarket,
  selectedStatus
)

const { prevCheckoutCommissionsAmount, prevCheckoutDataAmount } = usePrevCheckoutData(
  selectedAccommodationId,
  currency,
  formattedBookingComparisonStartDate,
  formattedBookingComparisonEndDate,
  selectedMarket,
  selectedStatus
)

const { bookingsAmount } = useBookingsAmount(
  selectedAccommodationId,
  currency,
  formattedBookingStartDate,
  formattedBookingEndDate,
  selectedMarket,
  selectedStatus
)

const { prevBookingsAmount } = usePrevBookingsAmount(
  selectedAccommodationId,
  currency,
  formattedBookingComparisonStartDate,
  formattedBookingComparisonEndDate,
  selectedMarket,
  selectedStatus
)

const bookingKpis = computed(() => {
  const kpis = [
    {
      id: 'bookingDate',
      label: 'rc_table_bookings',
      number: bookingsAmount.value,
      progression: calculatePercentageChange(prevBookingsAmount.value, bookingsAmount.value),
      subtitle: !selectedBookingPeriod.value.includes('next'),
      subtitleText: 'rc_bookings_received',
    },
    {
      id: 'checkin',
      label: 'rc_booking_details_checkins_title',
      number: checkinDataAmount.value,
      progression: calculatePercentageChange(prevCheckinDataAmount.value, checkinDataAmount.value),
      subtitle: true,
      subtitleText: selectedBookingPeriod.value.includes('next') ? 'rc_check_outs_scheduled' : '',
    },
    {
      id: 'checkout',
      label: 'rc_booking_details_checkouts_title',
      number: checkoutDataAmount.value,
      progression: calculatePercentageChange(prevCheckoutDataAmount.value, checkoutDataAmount.value),
      subtitle: true,
      subtitleText: selectedBookingPeriod.value.includes('next')
        ? 'rc_check_outs_scheduled'
        : 'rc_check_outs_completed',
    },
  ]
  if (props.bookingTableCampaignType !== 'cpc') {
    return [
      ...kpis,
      {
        id: 'commissions',
        label: 'rc_booking_details_commission_title',
        number: checkoutCommissionsAmount.value,
        progression: calculatePercentageChange(prevCheckoutCommissionsAmount.value, checkoutCommissionsAmount.value),
        subtitle: true,
        subtitleText: selectedBookingPeriod.value.includes('next')
          ? 'rc_commmission_estimated'
          : 'rc_commission_subtext',
      },
    ]
  } else {
    return kpis
  }
})

const calculatePercentageChange = (oldValue, newValue) => {
  if (oldValue === 0 && newValue === 0) return 0
  else if (oldValue === 0) return 100
  else return ((newValue - oldValue) / oldValue) * 100
}

const getKpiOrder = name => {
  if (name === 'bookings') return 0
  if (name === 'visits') return 1
  if (name === 'revenue') return 2
  if (name === 'cpa') return 3
}
const kpiTabs = computed(() => {
  const tabMap = ['bookings', 'visits', 'revenue']
  if (props.overviewTableCampaignType === 'cpc') {
    tabMap.push('cpa')
  }
  const kpis = props.prettyKpis
    .map(item => {
      return {
        ...item,
        order: getKpiOrder(item.key),
      }
    })
    .filter(item => tabMap.includes(item.key))

  return kpis.sort((a, b) => a.order - b.order)
})

const columns = computed(() => {
  // Check type type to display proper columns
  if (props.bookingTableCampaignType === 'cpa') {
    return cpaColumns
  } else if (props.bookingTableCampaignType === 'cpc') {
    return cpcColumns
  } else {
    return bothColumns
  }
})
</script>
<style scoped>
button:hover .table {
  fill: var(--tw-color-white);
  stroke: var(--tw-color-white);
}
button:hover .graph {
  stroke: var(--tw-color-white);
}
</style>
